import axios from 'axios';
import { getDefaultStore } from 'jotai';
import {
  authUserMetaAtom,
  delegateAccessEndReasonAtom,
  delegateAccessFailureNoticeAtom,
  viewingAsAtom,
  homeUserMetaAtom,
} from '@/store/authAtoms';
import { patientProfileAtom } from '@/store/patientProfileAtom';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { getActiveLanguage } from '@/store/langAtom';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { getCsrfToken, setCsrfToken } from '@/lib/csrf';
import { env } from '@/lib/env';

export const api = axios.create({
  baseURL: env.apiUrl,
  withCredentials: true,
});

// Controllers in the delegate "viewing/scheduling/messaging" scope (mirrors
// SessionTypeAuthorizationHandler.IsAllowedForDelegate in PortalApi). Every other request —
// Account, Patient (profile/insurance/security-questions/health-restrictions), MedicalRecordAccess,
// Authentication, MFA — is deliberately left off this list: matching Gen2, those pages always
// reflect the logged-in user's own identity, never the actively-viewed delegate patient, so the
// delegate bearer token must not be attached to them (the browser's own session cookie carries
// the home identity instead).
const delegateScopedPrefixes = [
  '/Worklist/', '/Study/', '/Order/', '/Scheduling/', '/DigitalForm/', '/Messages/',
  '/MedicalRecord/', '/Lookups/', '/Organization/', '/Legal/', '/Help/',
  '/Attachment/', '/PACS/',
];

function isDelegateScoped(url: string | undefined): boolean {
  if (!url) return false;
  let pathname = url;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    // Fall back to the raw value; axios normally provides either a path or URL.
  }
  if (pathname.startsWith('/api/')) pathname = pathname.slice(4);
  return delegateScopedPrefixes.some((prefix) => pathname.startsWith(prefix));
}

const bypassAuthRedirectPaths = new Set([
  '/Authentication/Login',
  '/Authentication/MFA/Verify',
  '/Authentication/ICode/Lookup',
  '/Authentication/ICode/Verify',
  '/Authentication/CompleteAfterICodeTerms',
  '/Authentication/SessionStatus',
  '/Authentication/RemoteAccessLogin',
  '/Help/Config',
  '/Help/SubmitRequest',
  '/SystemMessages/NotifyMe',
  '/ExamShare/ValidateAccess',
  // A 401 here means the practice has no document configured — the guard handles
  // this as isError and lets the user through. Without this bypass the interceptor
  // would wipe auth state and redirect to /home, causing an infinite login loop.
  '/Legal/PendingTerms',
  // AcceptTerms is called with a scoped setupToken in the mfa_setup flow; a 401
  // (expired token) must surface as a component error, not a portal logout.
  '/Legal/AcceptTerms',
  // Authorized-access invite acceptance authenticates the invitee before any session
  // exists. A 401 here is the credential result (invalid/locked/disabled), not an
  // expired session — AcceptInvite maps it to a message and must stay on the page.
  '/AuthorizedAccess/LinkAccount',
  '/AuthorizedAccess/SetCredentials',
]);

// Any URL whose path starts with one of these prefixes bypasses the 401 redirect.
// Engage endpoints use a short-lived scoped token — a 401 there should surface
// as an error in the component, not trigger a portal logout + redirect.
// MFA Settings/TOTP setup endpoints are called with a scoped setupToken before any
// BFF session exists; a 401 (e.g. expired setupToken) must surface as a component
// error, not a portal logout.
const bypassAuthRedirectPrefixes = [
  '/Engage/',
  '/Authentication/MFA/Settings/',
  '/Authentication/MFA/TOTP/',
];

// Page paths (window.location) where a 401 on ANY request should NOT trigger a
// portal redirect. These are public pages — bootstrap calls may return 401
// for unauthenticated visitors and must not kick them back to /home.
const bypassAuthRedirectPagePrefixes = [
  '/share/',
  '/examshareaccess/',
  '/home/examshareaccess/',
  '/schedule-attachment-upload/',
  '/gfe-request',
  // Self-Reg Exam-first may call catalog endpoints before a guest session exists.
  '/self-reg',
];

function isBypassUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (bypassAuthRedirectPaths.has(url)) return true;
  return bypassAuthRedirectPrefixes.some((prefix) => url.startsWith(prefix));
}

function clearAuthAndRedirect(): void {
  const s = getDefaultStore();
  // Never mutate auth state or navigate during a remote access session — the admin
  // authenticated via RemoteAccessLogin and the session context is still valid.
  if (s.get(remoteAccessContextAtom) !== null) return;
  s.set(authUserMetaAtom, null);
  s.set(patientProfileAtom, null);
  setCsrfToken(null);
  window.location.href = '/home';
}

api.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken();
  if (csrfToken) config.headers['X-XSRF-TOKEN'] = csrfToken;
  config.headers['User-Language'] = getActiveLanguage();
  // X-Site-Group must be site_group_code (Facility.id), never l_site_group.description (Facility.name).
  // PortalApi.RequestContext also normalises a mistaken description back to the code.
  const facility = getDefaultStore().get(selectedFacilityAtom);
  if (facility?.id) config.headers['X-Site-Group'] = facility.id;

  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: unknown) => {
    if (!axios.isAxiosError(err))
      return Promise.reject(err);

    const status = err.response?.status;
    const store = getDefaultStore();

    if (status === 401 && isBypassUrl(err.config?.url)) return Promise.reject(err);

    const sessionExpired = err.response?.headers?.['x-session-expired'];
    if (!sessionExpired && (status === 401 || status === 403) && store.get(viewingAsAtom) && isDelegateScoped(err.config?.url)) {
      const homeUser = store.get(homeUserMetaAtom);
      if (homeUser) store.set(authUserMetaAtom, homeUser);
      store.set(homeUserMetaAtom, null);
      store.set(viewingAsAtom, null);
      store.set(patientProfileAtom, null);
      localStorage.removeItem('delegate_token');
      const endReason = err.response?.headers?.['x-delegate-context-invalid'];
      store.set(delegateAccessEndReasonAtom, endReason === 'revoked' ? 'revoked' : 'expired');
      store.set(delegateAccessFailureNoticeAtom, (current) => current + 1);
      return Promise.reject(err);
    }

    if (status !== 401)
      return Promise.reject(err);

    const path = window.location.pathname.toLowerCase();
    if (bypassAuthRedirectPagePrefixes.some((p) => path.startsWith(p))) return Promise.reject(err);

    // Remote access sessions intentionally hit endpoints that 401 for NoAccount patients
    // (e.g. MessagesController requires user_key). Let the error surface in the component
    // rather than wiping the admin's session.
    if (getDefaultStore().get(remoteAccessContextAtom) !== null) return Promise.reject(err);

    clearAuthAndRedirect();
    return Promise.reject(err);
  },
);
